import { api } from './client'
import type {
    ApiEnvelope,
    DashboardData,
    DashboardFilterParams,
} from '@/types/api'

// --- 1. INTERFACES ---

export interface SurveySubStatItem {
    surveyTypeName: string
    totalRatings: number
}

export interface ReceptionRateItem {
    robotName?: string
    unitName?: string
    totalReceptions: number
}

export interface ReceptionDurationItem {
    robotName?: string
    unitName?: string
    duration: number
}

export interface SurveyStatsTargetItem {
    robotId?: string
    robotName?: string
    unitId?: string
    unitName?: string
    totalRatings: number
    surveyStatistics?: SurveySubStatItem[]
}

export interface SurveyStatsPeriodItem {
    period: string
    surveyStatistics?: SurveySubStatItem[]
}

export interface ProblemNoteStatItem {
    problemNote?: string
    totalSatisfiedRatings?: number
    count?: number
}

export interface BackendDashboardData {
    totalRobots: number
    totalReceptions: number
    totalSessionDuration: number
    totalRatings: number
    totalSatisfiedRatings: number
    totalNeutralRatings: number | null
    totalUnsatisfiedRatings: number | null

    receptionRateByRobots?: ReceptionRateItem[] | null

    receptionDurationByRobots?: ReceptionDurationItem[] | null
    receptionDurationByUnits?: ReceptionDurationItem[] | null

    surveyStatsByRobot?: SurveyStatsTargetItem[] | null
    surveyStatsByUnit?: SurveyStatsTargetItem[] | null

    surveyStatsByWeek?: SurveyStatsPeriodItem[] | null
    surveyStatsByMonth?: SurveyStatsPeriodItem[] | null

    surveysByProblemNoteStats?: ProblemNoteStatItem[] | null
}

// --- 2. HELPER FUNCTIONS ---

function unwrapResponse<T>(payload: any): T {
    if (payload?.responseData !== undefined) {
        return payload.responseData
    }
    if (payload?.result !== undefined) {
        return payload.result
    }
    throw new Error('Cấu trúc dữ liệu response không hợp lệ')
}

/**
 * Trích xuất số lượng đánh giá dựa trên `surveyTypeName` trong mảng lồng `surveyStatistics`
 */
function getStatByType(
    statsList: SurveySubStatItem[] | undefined,
    typeName: string
): number {
    if (!statsList || !Array.isArray(statsList)) return 0
    const found = statsList.find(
        (item) => item.surveyTypeName.trim().toLowerCase() === typeName.trim().toLowerCase()
    )
    return found?.totalRatings || 0
}

/**
 * Chuyển đổi mảng dữ liệu theo chu kỳ (Tuần / Tháng) từ backend sang định dạng cho biểu đồ
 */
function mapPeriodStats(items?: SurveyStatsPeriodItem[] | null) {
    return (items || []).map((item) => ({
        date: item.period,
        statDate: item.period,
        reviewCount: getStatByType(item.surveyStatistics, 'Tổng'),
        satisfiedCount: getStatByType(item.surveyStatistics, 'Hài lòng'),
        neutralCount: getStatByType(item.surveyStatistics, 'Bình thường'),
        unsatisfiedCount: getStatByType(item.surveyStatistics, 'Không hài lòng'),
    }))
}

// --- 3. MAIN FETCH FUNCTION ---

export async function fetchDashboardData(
    params?: DashboardFilterParams,
    signal?: AbortSignal
): Promise<DashboardData> {
    try {
        const response = await api.get<ApiEnvelope<BackendDashboardData>>(
            '/v1/stat/dashboard',
            {
                params,
                signal,
            }
        )

        const rawData = unwrapResponse<BackendDashboardData>(response.data)

        const calcPercent = (count: number, total: number) =>
            total > 0 ? Math.round((count / total) * 100) : 0

        const totalRatings = rawData.totalRatings || 0
        const satisfiedCount = rawData.totalSatisfiedRatings || 0
        const unsatisfiedCount = rawData.totalUnsatisfiedRatings || 0
        const neutralCount =
            rawData.totalNeutralRatings ??
            Math.max(0, totalRatings - satisfiedCount - unsatisfiedCount)

        const satisfactionWeek = mapPeriodStats(rawData.surveyStatsByWeek)
        const satisfactionMonth = mapPeriodStats(rawData.surveyStatsByMonth)

        return {
            // 1. Thẻ chỉ số tổng quan
            stats: {
                totalRobots: rawData.totalRobots || 0,
                totalReceptions: rawData.totalReceptions || 0,
                totalSessionDuration: rawData.totalSessionDuration || 0,
                totalReviews: totalRatings,
                satisfiedCount,
                satisfiedPercent: calcPercent(satisfiedCount, totalRatings),
                neutralCount,
                unsatisfiedCount,
                unsatisfiedPercent: calcPercent(unsatisfiedCount, totalRatings),
            },

            // 2. Biểu đồ thời gian tương tác (Duration)
            receptionDurationByRobots: (
                rawData.receptionDurationByRobots ||
                []
            ).map((item) => ({
                fullName: item.robotName || 'Khác',
                robotName: item.robotName,
                time: item.duration || 0,
                totalInteractionTime: item.duration || 0,
            })),

            // 3. Biểu đồ lượt đón tiếp (Receptions)
            receptionRateByRobots: (
                rawData.receptionRateByRobots ||
                []
            ).map((item) => ({
                robotName: item.robotName,
                count: item.totalReceptions || 0,
                totalReceptions: item.totalReceptions || 0,
            })),

            // 4. Biểu đồ đánh giá theo thời gian
            satisfactionWeek,
            satisfactionMonth,
            satisfaction: satisfactionWeek.length > 0 ? satisfactionWeek : satisfactionMonth,

            // 5. Biểu đồ phân bổ đánh giá theo đối tượng Robot
            surveyStatsByTarget: (
                rawData.surveyStatsByRobot ||
                []
            ).map((item) => {
                const name =
                    item.robotName || item.robotId || item.unitName || item.unitId || 'Khác'
                return {
                    name,
                    totalRatings:
                        item.totalRatings || getStatByType(item.surveyStatistics, 'Tổng'),
                    satisfiedRatings: getStatByType(item.surveyStatistics, 'Hài lòng'),
                    neutralRatings: getStatByType(item.surveyStatistics, 'Bình thường'),
                    unsatisfiedRatings: getStatByType(item.surveyStatistics, 'Không hài lòng'),
                }
            }),

            // 6. Biểu đồ nguyên nhân phản ánh (Donut Chart)
            problemNoteStats: (rawData.surveysByProblemNoteStats || []).map(
                (item) => ({
                    label: item.problemNote || 'Khác',
                    value: item.totalSatisfiedRatings ?? item.count ?? 0,
                })
            ),
        } as any
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi lấy dữ liệu dashboard.'
        throw new Error(message)
    }
}